import { notification } from 'antd';
import { useStoreState } from 'hooks';
import { useState } from 'react';
import { useLogin } from 'store/auth';
import { CONSISTENCY_JOBS_URL } from 'utils/config';
import { shouldRenewTokenForDV } from 'utils/data-validation';

export const useStartConsistencyJob = (): {
  startConsistencyJob: (
    collectionId: string,
    ruleId?: string,
    attributes?: Record<string, string[]>
  ) => Promise<void>;
  isRunning: boolean;
} => {
  let token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);
  const expiry = useStoreState((state) => state.auth.expiry);
  const login = useLogin();
  const [isRunning, setIsRunning] = useState(false);

  const startConsistencyJob = async (
    collectionId: string,
    ruleId?: string,
    attributes?: Record<string, string[]>
  ): Promise<void> => {
    if (!collectionId.trim()) return;

    setIsRunning(true);

    try {
      if (shouldRenewTokenForDV(expiry)) {
        token = (await login({ tenant, useOIDC: true, forceRefresh: true }))
          .token;
      }

      const body = JSON.stringify({
        Id: collectionId,
        type: ruleId ? 'consistency-rule' : 'consistency-collection',
        schemaCollectionRefId: '',
        ...(ruleId ? { ruleId } : {}),
        ...(attributes && Object.keys(attributes).length > 0
          ? { attributes }
          : {}),
      });

      const res = await fetch(CONSISTENCY_JOBS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          project: `${tenant}`,
          cluster: `${cluster}`,
        },
        body,
      });

      if (!res.ok) {
        throw new Error(
          `Something went wrong with the consistency job: ${res.statusText}.`
        );
      }

      notification.success({
        message: 'In progress',
        description:
          'Consistency check is now running. This might take a couple of minutes.',
      });
    } catch (err: unknown) {
      notification.error({
        message: 'Could not start the consistency job',
        description: String(err),
      });
    } finally {
      setIsRunning(false);
    }
  };

  return { startConsistencyJob, isRunning };
};
